import { idbLoadImage } from '@sloyd/store-base';
import { Button, LoaderPage, Modal, useCustomEvent } from '@sloyd/ui';
import { useEffect, useState } from 'react';
import { downloadImageBase64, guid4, ROOT_MEDIA } from 'shared-base';
import { Actions, Content, Image, Wrapper } from './StyleTransferPreview.style';

const IS_DEMO = true;

export type StyleTransferPreviewProps = {
  styleTransferImageId: string;
  styleTransferStyle: string;
  onClose: () => void;
  onApply: () => void;
};

export function StyleTransferPreview(props: StyleTransferPreviewProps) {
  const { styleTransferImageId, onClose } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const onDownload = () => {
    const base64 = imageSrc?.split(',')[1];
    const filename = `${guid4()}.png`;
    downloadImageBase64(filename, base64);
  };

  useCustomEvent('STYLE_TRANSFER_READY', (ev) => {
    const { imageId } = ev;
    if (imageId !== styleTransferImageId) return;
    setIsLoading(false);
    setIsReady(true);
  });

  useEffect(() => {
    if (!isReady) return;

    (async () => {
      if (IS_DEMO) {
        setImageSrc(`${ROOT_MEDIA}/style-transfer/ST-010.png`);

        return;
      }

      const base64 = await idbLoadImage(styleTransferImageId);

      if (base64) {
        setImageSrc(`data:image/png;base64,${base64}`);
      } else {
        console.warn(`No image found for ${styleTransferImageId}`);
      }
    })();
  }, [isReady, styleTransferImageId]);

  function renderInner() {
    if (isLoading) {
      return (
        <LoaderPage>
          Performing Style Transfer... <span>(takes around 30s)</span>
        </LoaderPage>
      );
    }

    if (imageSrc) {
      return <Image src={imageSrc} alt='Style Transfer Result' />;
    }

    return <div>Image could not be loaded.</div>;
  }

  return (
    <Modal
      opened
      title='Style Transfer Preview'
      onClose={onClose}
      centered
      withCloseButton
      size='830px'
      styles={{
        content: {
          border: '1px solid #334',
        },
      }}
    >
      <Wrapper className='StyleTransferPreview-wrapper' data-testid='StyleTransferPreview-wrapper'>
        <Content>{renderInner()}</Content>
        <Actions>
          <Button variant='subtle' color='#333' onClick={onClose}>
            Close
          </Button>
          <Button disabled={!isReady} variant='default' onClick={onDownload}>
            Download
          </Button>
          <Button
            onClick={props.onApply}
            variant='gradient'
            disabled={!isReady}
            gradient={{ deg: 45, from: 'pink', to: 'yellow' }}
          >
            Apply
          </Button>
        </Actions>
      </Wrapper>
    </Modal>
  );
}

export default StyleTransferPreview;
